import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import BaseSelect from '../Select/BaseSelect';

const SELECT_ALL_VALUE = '__SELECT_ALL__';

/**
 * @component BaseCheckboxSelect
 *
 * Checkbox-based multi-select built on top of BaseSelect.
 * Fully controlled. No form-library awareness.
 *
 * @param {Object} props
 * @param {Array<string|number>} props.value
 * @param {(value: Array<string|number>) => void} props.onChange
 * @param {{ label: string, value: string|number, disabled?: boolean }[]} props.options
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showSelectAll=false]
 * @param {string} [props.selectAllLabel="Select all"]
 * @param {(option: Object, checked: boolean) => React.ReactNode} [props.renderOptionLabel]
 * @param {(selected: Array<any>, options: Array<Object>) => React.ReactNode} [props.renderValue]
 * @returns {React.ReactElement}
 */
export default function BaseCheckboxSelect(props: any) {
  const {
    value = [],
    onChange,
    options,

    placeholder = 'Select',
    disabled = false,

    showSelectAll = false,
    selectAllLabel = 'Select all',

    renderOptionLabel,
    renderValue,

    ...rest
  } = props;

  const selectedSet = new Set(value);

  const enabledValues = options.filter((opt) => !opt.disabled).map((opt) => opt.value);

  const isAllSelected = enabledValues.length > 0 && enabledValues.every((v) => selectedSet.has(v));

  const handleChange = (nextValue) => {
    // Ignore synthetic select-all value
    if (nextValue.includes(SELECT_ALL_VALUE)) {
      onChange(isAllSelected ? [] : enabledValues);
      return;
    }

    onChange(nextValue);
  };

  const defaultRenderOptionLabel = (option, checked) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Checkbox size='small' checked={checked} disableRipple />
      <span>{option.label}</span>
    </Box>
  );

  const resolvedRenderValue =
    renderValue ??
    ((selected) => {
      if (!selected.length) return placeholder;

      const labels = options.filter((opt) => selected.includes(opt.value)).map((opt) => opt.label);

      return labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`;
    });

  return (
    <BaseSelect
      {...rest}
      multiple
      disabled={disabled}
      value={value}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      renderValue={(selected) => resolvedRenderValue(selected, options)}
      renderOption={(option) => {
        const checked = selectedSet.has(option.value);

        return renderOptionLabel
          ? renderOptionLabel(option, checked)
          : defaultRenderOptionLabel(option, checked);
      }}
      MenuProps={{
        MenuListProps: { dense: true },
      }}
    >
      {showSelectAll && (
        <MenuItem value={SELECT_ALL_VALUE}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              size='small'
              checked={isAllSelected}
              indeterminate={!isAllSelected && value.length > 0}
              disableRipple
            />
            <strong>{selectAllLabel}</strong>
          </Box>
        </MenuItem>
      )}
    </BaseSelect>
  );
}
